import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect, useRouteMatch, Switch, Route } from "react-router-dom";
import { Spinner } from "../../assets";
import { getVendor, getVendorProducts, getVendorAddOns } from "../../actions/vendor";
import AboutVendor from "./AboutVendor";
import { getBasket } from "../../actions/basket";
import Basket from "./Basket";
import { vendorPath } from "../../helpers";
import PoweredBy from "../PoweredBy";
import { Helmet } from "react-helmet";

import "./style.scss";
import UnderPanel from "./UnderPanel";
import { useDeviceSize, MOBILE } from "../../assets/hooks/useDeviceSize";
import UnderPanelMini from "./UnderPanelMini";
import { CategoryListHorizontal } from "./CategoryList";
import ProductList from "../ProductList";
import CheckoutSuccess from "./CheckoutSuccess";
import Login from "../Login";

export default function Vendor() {

	const { params } = useRouteMatch();
	const slug = params.vendor;
	const { device } = useDeviceSize();
	const canvas = useRef(null);

	const dispatch = useDispatch();
	const {
		session: { user },
		vendor
	} = useSelector(state => ({
		session: state.session,
		vendor: state.vendor,
		basket: state.basket
	}));

	// For getting the vendor. 
	useEffect(() => {
		dispatch(getVendor({ slug }));
	}, [slug, dispatch]);

	// For getting the basket for each vendor.
	const userId = user ? user.id : null;
	useEffect(() => {
		if (vendor.id && userId) dispatch(getBasket());
	}, [vendor.id, userId, dispatch]);

	// Getting a vendor's products
	useEffect(() => {
		if (vendor.id) {
			dispatch(getVendorProducts());
			dispatch(getVendorAddOns());
		}
	}, [slug, vendor.id, dispatch]);

	if (vendor.loading) return <Spinner />;
	if (!vendor.id) return <Redirect to="/me" />;

	return <div className="vendor-view">

		<Helmet>
			<meta name="description" content={vendor.description} />
			<meta property="og:description" content={vendor.description} />
			<title>{vendor.name} | ORDERABL+</title>
			<meta property="og:site_name" content={`${vendor.name} on ORDERABL+`} />
			<meta property="og:title" content={`${vendor.name} on ORDERABL+`} />
			<meta property="og:image" content={vendor.logo ? vendor.logo.url : "/media/splash_1_a.png"} />
			<meta property="og:url" content={`https://be.orderabl.com${vendorPath(vendor.slug)}`} />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:image:alt" content={`${vendor.name} on, orderabl`} />
		</Helmet>

		{/* Vendor info */}
		<AboutVendor vendor={vendor} collapsed={true} />

		{/* Canvas for confetti events */}
		<canvas ref={canvas} className="confetti-canvas" />

		{/* When not logged in, prompt login */}
		{/* {!user && <LoginBox />} */}

		{/* Otherwise shop shop routes */}
		<Switch>

			{/* Successful payments get redirected here */}
			<Route path="/(with|s|from|store|\+|plus)/:vendor/success" render={router => (
				<CheckoutSuccess canvas={canvas} />
			)} />

			{/* Shop proper is here */}
			<Route path="/(with|s|from|store|\+|plus)/:vendor" render={router => <>

				{/* Show the shop */}
				<div className="shop-main">

					{/* Horizontal category list on tablet/mobile */}
					<Route
						path={vendorPath(vendor.slug)}
						exact
						render={router => (
							<CategoryListHorizontal />
						)}
					/>

					<div className="shop-columns maximum-width flexy">

						{/* If we're on a mobile device, split the views into pages */}
						{device === MOBILE ?
							(
								<Switch>
									{userId && <Route
										path={`${vendorPath(vendor.slug)}/basket`}
										render={router => (
											<Basket />
										)}
									/>}
									<Route
										path={vendorPath(vendor.slug)}
										render={router => (
											<ProductList
												products={vendor.products}
												expandedByDefault={true}
											/>
										)}
									/>
								</Switch>
							) : <>
								{/* Otherwise, render both along side each other */}
								<ProductList
									products={vendor.products}
									expandedByDefault={true}
								/>
								{userId
									? (
										<Basket />
									) : (
										<div className="sidebarLogin" >
											<div className="panel">
												<section>
													<h4>Log in / Sign up</h4>
													<div className="inner">
														Create your <span className="featureFont" style={{ fontSize: "1.1rem" }}>Orderabl<span className="brand-c">+</span> </span> account with one click to buy from {vendor.name}.
													<br /><br />
														<Login />
													</div>
												</section>
											</div>
										</div>
									)
								}
							</>
						}

						{/* Send back to main shop page if not on mobile, and on basket view. */}
						{device !== MOBILE && <Route
							path={`${vendorPath(vendor.slug)}/basket`}
							render={router =>
								<Redirect to={vendorPath(vendor.slug)} />
							}
						/>}
					</div>

				</div>

				{/* Full mobile lower panel with basket and user details */}
				{user && device === MOBILE && <UnderPanel />}
				{/* Account link for tablet and above. */}
				{user && device !== MOBILE && <UnderPanelMini />}
			</>} />

		</Switch>

		<PoweredBy branded={true} />
	</div>
}